import { useNotificationStore } from "../store/notificationStore";
import type { NotificationRecord } from "../store/notificationModalStore";
import { useNotificationModalStore } from "../store/notificationModalStore";
import { markMessageAsRead } from "ecp-notifications-sc-subscriber";
import { createLogger } from "../utils/logger";
import {
  formatTimestamp,
  formatNotificationTime,
} from "../utils/formatNotificationTime";
import { sanitize, getVariant, type BadgeVariant } from "../utils/notificationUtils";



const logger = createLogger("mapToMastheadNotifications");

interface MastheadOptions {
  token?: string;
  env?: string;
  initialDays?: number;
  onMarkAsRead?: (id: string) => void;
}

export interface MastheadNotificationItem {
  id: string | undefined;
  icon: undefined;
  header: string;
  messageText: string;
  timestamp: string;
  badge?: { label: string; variant: BadgeVariant };
  href: string;
  isRead: boolean;
  onNotificationClick: () => Promise<void>;
  messageLink: { label: string; href: string; onClick: () => void };
}

export interface MastheadNotificationsResult {
  notifications: undefined;
  footerClickCallback: undefined;
  notificationsViewAllLink: string;
  NotificationsOverride: undefined;
  ecpNotifications: MastheadNotificationItem[];
  historyHref: string;
}

export function ecpNotificationsToMastheadNotifications(
  notifications: NotificationRecord[],
  options: MastheadOptions = {},
): MastheadNotificationsResult {
  if (!Array.isArray(notifications)) {
    logger.warn("Invalid notifications array");
    return {
      notifications: undefined,
      footerClickCallback: undefined,
      notificationsViewAllLink: "",
      NotificationsOverride: undefined,
      ecpNotifications: [],
      historyHref: `/notification-data?env=${options.env ?? ""}&initialDays=${options.initialDays ?? ""}`,
    };
  }

  const mastheadNotifications = notifications.map((n) =>
    notificationToMastheadItem(n, options),
  );

  return {
    notifications: undefined,
    footerClickCallback: undefined,
    notificationsViewAllLink: "",
    NotificationsOverride: undefined,
    ecpNotifications: mastheadNotifications,
    historyHref: `/notification-data?env=${options.env ?? ""}&initialDays=${options.initialDays ?? ""}`,
  };
}

export function notificationToMastheadItem(
  notification: NotificationRecord,
  { token, env, onMarkAsRead }: MastheadOptions = {},
): MastheadNotificationItem {
  const id = notification?.requestId;
  const isRead = Boolean(notification?.isRead);

  const handleNotificationClick = async () => {
    if (!isRead && id) {
      useNotificationStore.getState().updateNotification(id, { isRead: true });


      //const rawFilters = notification?.filters as string | undefined;
      //const filter = (!rawFilters || rawFilters === "None") ? null : rawFilters;

       if (token && env) {
        try {
          await markMessageAsRead(token, env, {
            requestId: id,
            requestEpoch: String(notification?.requestEpoch || ""),
           // ...(filter ? { filter } : {}),
            isRead: true,
          });
        } catch (error) {
          logger.error("Failed to mark notification as read:", error);
        }
      }
    }
    if (onMarkAsRead && id) onMarkAsRead(id);
  };

  return {
    id,
    icon: undefined,
    header: sanitize(notification?.title || "Notification"),
    messageText: sanitize(notification?.message || ""),
    timestamp: formatNotificationTime(
      formatTimestamp(notification) ||
      (notification?.timestamp as string) ||
      "",
    ),
    badge:
      notification?.priority && notification.priority.toLowerCase() !== "low"
        ? {
          label: sanitize(notification.priority),
          variant: getVariant(notification.priority),
        }
        : undefined,
    href: "#",
    isRead,
    onNotificationClick: handleNotificationClick,
    messageLink: {
      label: "View details",
      href: "#",
      onClick: () => {
        void handleNotificationClick();
        viewNotificationMessage(notification);
      },
    },
  };
}

export function viewNotificationMessage(notification: NotificationRecord) {
  useNotificationModalStore.getState().openModal(notification);
}
